import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import Icon from 'utils/components/icon';
import styles from './Input.module.scss';

interface IInputProps {
  isBorderless?: boolean;
  value?: string;
  addonBefore?: React.ReactNode;
  addonAfter?: React.ReactNode;
  onChange?: (value: string) => void;
  onClear?: () => void;
}

const Input = React.forwardRef<
  HTMLInputElement,
  Omit<React.AllHTMLAttributes<HTMLInputElement>, 'onChange'> & IInputProps
>(function Input(
  {
    isBorderless = false,
    value = '',
    className,
    addonBefore,
    addonAfter,
    onClear,
    onChange = () => {},
    ...restProps
  },
  ref
): JSX.Element {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleClear = () => {
    setInputValue('');
    onChange('');
    onClear();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value }
    } = event;

    setInputValue(value);
    onChange(value);
  };

  return (
    <div className={cx(styles.inputContainer, className)}>
      {addonBefore && <span className={styles.addonBefore}>{addonBefore}</span>}
      <input
        {...restProps}
        className={cx(styles.input, { [styles.inputBorderless]: isBorderless })}
        onChange={handleChange}
        value={inputValue}
        ref={ref}
      />
      {onClear && value && (
        <Icon
          data-testid="input-clear-icon"
          className={styles.clearIcon}
          name="close"
          onClick={handleClear}
        />
      )}
      {addonAfter && <span className={styles.addonAfter}>{addonAfter}</span>}
    </div>
  );
});

export default Input;
